import { h } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Card, Model, Msg, TagDef, TagValue } from "../types";
import { formatValue, parseDraft, pickerType, systemTagToDef } from "../editor/values";
import { describeDate, isDateValue } from "../dates";

export function viewMetadata(model: Model, card: Card, dispatch: (msg: Msg) => void): VNode {
  const registry = new Map(model.registry.map((d) => [d.name, d]));
  // System tags come from the backend via the registry endpoint — never
  // hardcoded here.
  const systemByName = new Map(model.systemTags.map((t) => [t.name, t]));

  // Server-owned system tags (e.g. created_at/updated_at) → read-only badges,
  // in their declared order.
  const readonly = model.systemTags
    .filter((t) => !t.assignable && t.name in card.tags)
    .map((t) => viewSystemBadge(t.name, card.tags[t.name]!, model.now));

  // Editable badges: registry tags (orphans hidden) plus assignable system tags
  // (e.g. template), both removable.
  const editable = Object.keys(card.tags)
    .filter((k) => {
      const sys = systemByName.get(k);
      return sys ? sys.assignable : registry.has(k);
    })
    .sort((a, b) => a.localeCompare(b))
    .map((k) => {
      const sys = systemByName.get(k);
      const def = sys ? systemTagToDef(sys) : registry.get(k)!;
      return viewBadge(k, card.tags[k]!, def, model, dispatch);
    });

  return h("div.metadata", [
    ...readonly,
    ...editable,
    model.tagPicker
      ? viewTagPicker(model, card, dispatch)
      : h("button.chip.metadata__add", { on: { click: () => dispatch({ type: "OpenTagPicker" }) } }, "+ tag"),
  ]);
}

function viewSystemBadge(key: string, value: TagValue, now: number): VNode {
  const { text, title } = displayValue(value, now);
  return h("span.chip.badge--system", { style: badgeColors(key) }, [
    h("span.badge__key", key),
    h("span.badge__value", { attrs: title ? { title } : {} }, text || "…"),
  ]);
}

// A date shows relative to now, with the exact time as its tooltip. Other
// values show as they are.
function displayValue(value: TagValue, now: number): { text: string; title?: string } {
  return isDateValue(value) ? describeDate(value, now) : { text: formatValue(value) };
}

function viewBadge(
  key: string,
  value: TagValue,
  def: TagDef,
  model: Model,
  dispatch: (msg: Msg) => void
): VNode {
  const valueNode = viewValue(key, value, def, model, dispatch);
  return h("span.chip", { style: badgeColors(key) }, [
    h("span.badge__key", key),
    ...(valueNode ? [valueNode] : []),
    h("span.badge__delete", { on: { click: () => dispatch({ type: "DeleteTag", key }) } }, "×"),
  ]);
}

function viewValue(
  key: string,
  value: TagValue,
  def: TagDef,
  model: Model,
  dispatch: (msg: Msg) => void
): VNode | null {
  if (def.valueType === "none") return null;
  const editingTag = model.editingTag;

  // A press on the value starts the editor. The editor starts on mousedown, not
  // on click: the open editor of another value closes at once and changes the
  // layout, so a click could end on a different element.
  if (editingTag?.key !== key) {
    const { text, title } = displayValue(value, model.now);
    return h(
      "span.badge__value" + (text ? "" : ".badge__value--empty"),
      {
        attrs: title ? { title } : {},
        on: {
          mousedown: (e: Event) => {
            // The render gives the focus to the new input during this handler.
            // Without preventDefault, the browser then moves the focus away,
            // and the new input commits at once.
            e.preventDefault();
            dispatch({ type: "EditTagValue", key });
          },
        },
      },
      text || "…"
    );
  }

  const draft = editingTag.draft;
  const valid = parseDraft(def, draft) !== null;
  const on = {
    input: (e: Event) => dispatch({ type: "EditTagDraft", draft: (e.target as HTMLInputElement).value }),
    // A click away stores a valid value and discards an invalid one.
    blur: () => dispatch({ type: "CommitTagEdit", key }),
    keydown: (e: KeyboardEvent) => {
      // An invalid draft keeps the editor open.
      if (e.key === "Enter" && valid) dispatch({ type: "CommitTagEdit", key });
      if (e.key === "Escape") dispatch({ type: "CancelTagEdit" });
    },
  };

  const picker = pickerType(def);
  if (picker) {
    return h("input.badge__value-input", {
      attrs: { type: picker === "date" ? "date" : "datetime-local", value: draft },
      hook: {
        insert: (vn) => {
          const el = vn.elm as HTMLInputElement;
          el.focus();
          // The press on the value permits the picker to open. A browser that
          // refuses it still shows the input, so the error has no effect.
          try {
            el.showPicker();
          } catch {}
        },
      },
      on,
    });
  }

  const input = h("input.badge__value-input", {
    // Set the invalid class with the class module, not the selector. Thus the
    // element stays the same, and the caret does not reset at each key.
    class: { "badge__value-input--invalid": !valid },
    attrs: { value: draft, size: inputSize(draft) },
    hook: {
      insert: (vn) => {
        const el = vn.elm as HTMLInputElement;
        el.focus();
        // The first key replaces a predefined value. A free value stays, so
        // the caret goes to the end of it.
        if (def.values.length > 0) el.select();
        else el.setSelectionRange(el.value.length, el.value.length);
      },
    },
    on,
  });

  // No predefined values → plain input. Otherwise show a suggestion list below.
  if (def.values.length === 0) return input;

  // Until the user types, the draft is the set value. Filtering by it would
  // hide the other values, so the list shows all of them.
  const q = draft.toLowerCase();
  const matches =
    draft === formatValue(value)
      ? def.values
      : def.values.filter((v) => formatValue(v).toLowerCase().includes(q));
  return h("span.badge__value-edit", [
    input,
    h(
      "ul.badge__suggest",
      matches.length === 0
        ? [h("li.badge__suggest-empty", "No match")]
        : matches.slice(0, 8).map((v) =>
            h(
              "li.badge__suggest-item",
              {
                // preventDefault keeps input focus, so blur won't race the pick.
                on: {
                  mousedown: (e: Event) => {
                    e.preventDefault();
                    dispatch({ type: "SetTagValue", key, value: v });
                  },
                },
              },
              formatValue(v)
            )
          )
    ),
  ]);
}

function viewTagPicker(model: Model, card: Card, dispatch: (msg: Msg) => void): VNode {
  const draft = model.tagPicker!.draft.toLowerCase();
  // Pickable names: registry tags + assignable system tags (e.g. template).
  const names = [
    ...model.registry.map((d) => d.name),
    ...model.systemTags.filter((t) => t.assignable).map((t) => t.name),
  ];
  const candidates = names
    .filter((name) => !(name in card.tags) && name.toLowerCase().includes(draft))
    .sort((a, b) => a.localeCompare(b));

  return h("span.tag-picker", [
    h("input.tag-picker__input", {
      attrs: { placeholder: "tag…" },
      props: { value: model.tagPicker!.draft },
      hook: { insert: (vn) => (vn.elm as HTMLInputElement).focus() },
      on: {
        input: (e: Event) =>
          dispatch({ type: "SetTagPickerDraft", draft: (e.target as HTMLInputElement).value }),
        blur: () => setTimeout(() => dispatch({ type: "CloseTagPicker" }), 150),
        keydown: (e: KeyboardEvent) => {
          if (e.key === "Escape") dispatch({ type: "CloseTagPicker" });
          // Enter picks the sole/first remaining candidate.
          if (e.key === "Enter" && candidates[0]) dispatch({ type: "PickTag", name: candidates[0] });
        },
      },
    }),
    h(
      "ul.tag-picker__list",
      candidates.length === 0
        ? [h("li.tag-picker__empty", names.length === 0 ? "No tags defined" : "No match")]
        : candidates.slice(0, 8).map((name) =>
            h(
              "li.tag-picker__item",
              { on: { mousedown: () => dispatch({ type: "PickTag", name }) } },
              name
            )
          )
    ),
  ]);
}

// The hues of the card-link blue.
const LINK_HUE_START = 195;
const LINK_HUE_WIDTH = 50;

// Each tag key gets a pastel color from its hash, so that the key has the same
// color on all cards. The color skips the link hues, so that a tag cannot look
// like a link.
function badgeColors(key: string): Record<string, string> {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const spread = Math.abs(hash) % (360 - LINK_HUE_WIDTH);
  const hue = spread < LINK_HUE_START ? spread : spread + LINK_HUE_WIDTH;
  return {
    background: `hsl(${hue}, 65%, 92%)`,
    "border-color": `hsl(${hue}, 45%, 78%)`,
  };
}

// Input width follows content: `size` is in chars, +1 for the cursor, min 3.
function inputSize(draft: string): number {
  return Math.max(draft.length + 1, 3);
}
